import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Mail, LogOut, Inbox, ShieldAlert, Users as UsersIcon, Menu, FileText, Newspaper, MailPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, type AppRole } from "@/contexts/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import logo from "@/assets/ai-labbet-white.png";

type NavChild = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
};

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  badgeKey?: "newContacts";
  allowed: AppRole[];
  children?: NavChild[];
  /** Route prefix that marks this group as "active" / expanded */
  match?: string;
};

const navItems: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, allowed: ["admin", "editor"] },
  { to: "/admin/contacts", label: "Contact submissions", icon: Inbox, badgeKey: "newContacts", allowed: ["admin", "editor"] },
  {
    to: "/admin/posts",
    label: "Newsletter",
    icon: Newspaper,
    allowed: ["admin"],
    match: "/admin/posts",
    children: [
      { to: "/admin/posts", label: "Posts", icon: Newspaper },
      { to: "/admin/subscribers", label: "Subscribers", icon: MailPlus },
    ],
  },
  {
    to: "/admin/emails",
    label: "Email",
    icon: Mail,
    allowed: ["admin"],
    match: "/admin/emails",
    children: [
      { to: "/admin/emails", label: "Log", icon: FileText, end: true },
      { to: "/admin/emails/suppressions", label: "Suppressions", icon: ShieldAlert },
    ],
  },
  { to: "/admin/users", label: "Users", icon: UsersIcon, allowed: ["admin"] },
];

const AdminLayout = () => {
  const { user, signOut, role, isEditor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [newContacts, setNewContacts] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isEditor) return;
    let active = true;
    const loadCount = async () => {
      const { count } = await supabase
        .from("contact_submissions")
        .select("id", { count: "exact", head: true })
        .eq("status", "new");
      if (active) setNewContacts(count ?? 0);
    };
    loadCount();
    const interval = window.setInterval(loadCount, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [isEditor]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const badges = { newContacts };

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  const visibleItems = navItems.filter((item) => role && item.allowed.includes(role));

  const SidebarContent = (
    <>
      <Link to="/admin" className="flex items-center px-5 h-16 border-b border-border hover:bg-muted/40 transition-colors">
        <img src={logo} alt="AI-labbet" className="h-7 w-auto" />
      </Link>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const badgeValue = item.badgeKey ? badges[item.badgeKey] : null;
          const isGroup = !!item.children?.length;
          const groupActive = isGroup
            ? (item.children ?? []).some(
                (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"),
              )
            : false;

          if (!isGroup) {
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-primary/15 text-primary font-medium"
                      : "text-foreground/80 hover:bg-muted hover:text-foreground"
                  )
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {badgeValue !== null && badgeValue !== undefined && badgeValue > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                    {badgeValue}
                  </span>
                )}
              </NavLink>
            );
          }

          return (
            <div key={item.to} className="space-y-0.5">
              <Link
                to={item.to}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  groupActive
                    ? "text-foreground font-medium"
                    : "text-foreground/80 hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
              </Link>
              <div className="ml-[14px] pl-3 border-l border-border space-y-0.5">
                {item.children!.map((child) => {
                  const ChildIcon = child.icon;
                  return (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.end}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors",
                          isActive
                            ? "bg-primary/15 text-primary font-medium"
                            : "text-foreground/70 hover:bg-muted hover:text-foreground"
                        )
                      }
                    >
                      <ChildIcon className="w-3.5 h-3.5" />
                      <span>{child.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-3 pb-1 text-xs text-foreground/80 truncate" title={user?.email}>{user?.email}</div>
        <div className="px-3 pb-3 text-[10px] uppercase tracking-wider text-muted-foreground">
          {role === "admin" ? "Admin" : role === "editor" ? "Editor" : role}
        </div>
        <Button variant="outline" size="sm" className="w-full" onClick={handleSignOut}>
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-border bg-card/30 flex-col shrink-0">
        {SidebarContent}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14 border-b border-border bg-background/95 backdrop-blur">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 flex flex-col bg-card">
              {SidebarContent}
            </SheetContent>
          </Sheet>
          <Link to="/admin" className="text-sm font-semibold tracking-tight">
            AI-labbet · Admin
          </Link>
          <div className="w-9" aria-hidden />
        </header>

        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div
            className={cn(
              "container mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10",
              /^\/admin\/posts\/[^/]+/.test(location.pathname) ? "max-w-[1400px]" : "max-w-6xl",
            )}
          >
            <Outlet />
          </div>
        </main>

      </div>
    </div>
  );
};

export default AdminLayout;
